import { useForm } from "@tanstack/react-form";
import { t as tI18n } from "i18next";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { Switch } from "@/core/components/ui/switch";
import { Textarea } from "@/core/components/ui/textarea";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type {
  NotificationProviderCreateRequest,
  NotificationProviderDto,
} from "@/features/notification/api/dto";
import { useCreateNotificationProvider } from "@/features/notification/api/use-create-notification-provider";
import { useUpdateNotificationProvider } from "@/features/notification/api/use-update-notification-provider";
import {
  createNotificationProviderSchema,
  type NotificationProviderFormValues,
} from "@/features/notification/schemas/notification-provider.schema";
import { NOTIFICATION_CHANNELS } from "@/features/shared/api/enums";

const EMPTY_VALUES: NotificationProviderFormValues = {
  code: "",
  name: "",
  channel: "SMS",
  countryCode: "",
  weight: 50,
  priority: 1,
  isActive: true,
  configJson: "{}",
};

function toInitialValues(
  provider: NotificationProviderDto | null | undefined
): NotificationProviderFormValues {
  if (!provider) return EMPTY_VALUES;
  return {
    code: provider.code,
    name: provider.name,
    channel: provider.channel,
    countryCode: provider.countryCode ?? "",
    weight: provider.weight,
    priority: provider.priority,
    isActive: provider.isActive,
    configJson: provider.config ? JSON.stringify(provider.config, null, 2) : "{}",
  };
}

function toApiPayload(
  values: NotificationProviderFormValues
): NotificationProviderCreateRequest {
  let config: Record<string, unknown> | null = null;
  const trimmed = values.configJson.trim();
  if (trimmed) {
    try {
      config = JSON.parse(trimmed) as Record<string, unknown>;
    } catch {
      config = null;
    }
  }
  return {
    code: values.code.trim().toUpperCase(),
    name: values.name.trim(),
    channel: values.channel,
    countryCode: values.countryCode?.trim() || null,
    weight: Number(values.weight),
    priority: Number(values.priority),
    isActive: values.isActive,
    config,
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider?: NotificationProviderDto | null;
}

/**
 * Formulaire création/édition d'un fournisseur de transport
 * (`POST /NotificationProviders/add` ou `PUT /{id}`, guide v2.3 §6.7).
 * Preview `MutationConfirmPreview` avant l'appel API.
 */
export function NotificationProviderFormDialog({
  open,
  onOpenChange,
  provider,
}: Props) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- schéma re-fabriqué quand la langue change
  const schema = useMemo(() => createNotificationProviderSchema(), [i18n.language]);

  const create = useCreateNotificationProvider();
  const update = useUpdateNotificationProvider();
  const pending = create.isPending || update.isPending;
  const [previewPayload, setPreviewPayload] =
    useState<NotificationProviderCreateRequest | null>(null);

  const form = useForm({
    defaultValues: toInitialValues(provider),
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreviewPayload(toApiPayload(parsed.data));
    },
  });

  const isEditing = Boolean(provider?.id);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {isEditing
                ? t(
                    "notifications.providers.preview.updateTitle" as never,
                    {} as never
                  )
                : t(
                    "notifications.providers.create" as never,
                    {} as never
                  )}
            </DialogTitle>
          </DialogHeader>

          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field name="code">
              {(field) => (
                <div className="flex flex-col gap-1">
                  <Label htmlFor={field.name}>
                    {t(
                      "notifications.providers.form.code" as never,
                      {} as never
                    )}
                  </Label>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t(
                      "notifications.providers.form.codePlaceholder" as never,
                      {} as never
                    )}
                    disabled={isEditing}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="name">
              {(field) => (
                <div className="flex flex-col gap-1">
                  <Label htmlFor={field.name}>
                    {t(
                      "notifications.providers.form.name" as never,
                      {} as never
                    )}
                  </Label>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t(
                      "notifications.providers.form.namePlaceholder" as never,
                      {} as never
                    )}
                  />
                </div>
              )}
            </form.Field>

            <div className="grid grid-cols-2 gap-3">
              <form.Field name="channel">
                {(field) => (
                  <div className="flex flex-col gap-1">
                    <Label htmlFor={field.name}>
                      {t(
                        "notifications.providers.form.channel" as never,
                        {} as never
                      )}
                    </Label>
                    <Select
                      value={field.state.value}
                      onValueChange={(value) =>
                        field.handleChange(
                          value as NotificationProviderFormValues["channel"]
                        )
                      }
                    >
                      <SelectTrigger id={field.name}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {NOTIFICATION_CHANNELS.map((channel) => (
                          <SelectItem key={channel} value={channel}>
                            {channel}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form.Field>

              <form.Field name="countryCode">
                {(field) => (
                  <div className="flex flex-col gap-1">
                    <Label htmlFor={field.name}>
                      {t(
                        "notifications.providers.form.countryCode" as never,
                        {} as never
                      )}
                    </Label>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t(
                        "notifications.providers.form.countryCodePlaceholder" as never,
                        {} as never
                      )}
                    />
                  </div>
                )}
              </form.Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <form.Field name="weight">
                {(field) => (
                  <div className="flex flex-col gap-1">
                    <Label htmlFor={field.name}>
                      {t(
                        "notifications.providers.form.weight" as never,
                        {} as never
                      )}
                    </Label>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="number"
                      min={0}
                      max={100}
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                    />
                  </div>
                )}
              </form.Field>

              <form.Field name="priority">
                {(field) => (
                  <div className="flex flex-col gap-1">
                    <Label htmlFor={field.name}>
                      {t(
                        "notifications.providers.form.priority" as never,
                        {} as never
                      )}
                    </Label>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="number"
                      min={1}
                      value={field.state.value}
                      onChange={(e) =>
                        field.handleChange(Number(e.target.value))
                      }
                    />
                  </div>
                )}
              </form.Field>
            </div>

            <form.Field name="configJson">
              {(field) => (
                <div className="flex flex-col gap-1">
                  <Label htmlFor={field.name}>
                    {t(
                      "notifications.providers.form.config" as never,
                      {} as never
                    )}
                  </Label>
                  <Textarea
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t(
                      "notifications.providers.form.configPlaceholder" as never,
                      {} as never
                    )}
                    rows={5}
                    className="font-mono text-xs"
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="isActive">
              {(field) => (
                <div className="flex items-center justify-between pt-1">
                  <Label htmlFor={field.name}>
                    {t(
                      "notifications.providers.form.isActive" as never,
                      {} as never
                    )}
                  </Label>
                  <Switch
                    id={field.name}
                    checked={field.state.value}
                    onCheckedChange={(value) => field.handleChange(value)}
                  />
                </div>
              )}
            </form.Field>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <MutationConfirmPreview
        open={previewPayload !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewPayload(null);
        }}
        data={previewPayload ?? {}}
        title={
          isEditing
            ? t(
                "notifications.providers.preview.updateTitle" as never,
                {} as never
              )
            : t(
                "notifications.providers.preview.createTitle" as never,
                {} as never
              )
        }
        description={
          isEditing
            ? t(
                "notifications.providers.preview.updateDescription" as never,
                {} as never
              )
            : t(
                "notifications.providers.preview.createDescription" as never,
                {} as never
              )
        }
        confirmLabel={t("common.confirm")}
        t={(key: string) => String(tI18n(key as never, {} as never))}
        pending={pending}
        onConfirm={() => {
          if (!previewPayload) return;
          const handlers = {
            onSuccess: () => {
              notifSilence(
                t(
                  isEditing
                    ? ("notifications.providers.feedback.updated" as never)
                    : ("notifications.providers.feedback.created" as never),
                  {} as never
                )
              );
              setPreviewPayload(null);
              onOpenChange(false);
            },
            onError: (error: Error) => notifSilenceError(error.message),
          };
          if (isEditing && provider?.id) {
            update.mutate(
              { notificationProviderId: provider.id, body: previewPayload },
              handlers
            );
          } else {
            create.mutate(previewPayload, handlers);
          }
        }}
      />
    </>
  );
}
